import { Legend } from "./Legend";
import { butunFoizlar } from "./SegmentBar";
import { BOSH_USLUB, yumaloq } from "./scale";

export interface DonutSegment {
	key: string;
	value: number;
	/** CSS rang ifodasi — `holatVar()` yoki `var(--chart-*)`. */
	color: string;
	label: string;
}

export interface DonutProps {
	segments: DonutSegment[];
	/** Markazdagi asosiy qiymat (formatlangan matn). */
	centerValue: string;
	centerLabel: string;
	/** Diametr, px. */
	size?: number;
	/** Jami nol bo'lganda halqa ostidagi matn (reja imzosidan kengaytirilgan). */
	empty?: string;
	/** Legend va `aria-label` dagi qiymat formati (reja imzosidan kengaytirilgan). */
	formatValue?: (value: number) => string;
}

export interface DonutStop {
	color: string;
	/** Foiz, `[0, 100]`. */
	from: number;
	to: number;
}

const BOSHLIQ = 0.6;
const SIRT = "var(--chart-surface)";

/**
 * `conic-gradient` stoplari: har segmentdan keyin 0.6% sirt rangli
 * bo'shliq (oxirgisidan keyin ham — halqa aylanib birinchisiga
 * ulanadi).
 *
 * ⚠️ Stoplar UZLUKSIZ (`stops[i].from === stops[i-1].to`) va oxirgisi
 * AYNAN 100 da tugaydi — aks holda gradient oxirida shaffof yoriq
 * qolardi. Nol qiymatli segment tashlanadi; yagona segment bo'shliqsiz.
 * Juda kichik segmentda bo'shliq uning yarmidan oshmaydi.
 */
export function donutStops(segments: DonutSegment[]): DonutStop[] {
	const musbat = segments.filter((s) => s.value > 0);
	const jami = musbat.reduce((a, s) => a + s.value, 0);

	if (!(jami > 0)) {
		return [];
	}

	if (musbat.length === 1) {
		return [{ color: musbat[0]?.color ?? SIRT, from: 0, to: 100 }];
	}

	const natija: DonutStop[] = [];
	let boshi = 0;
	let yigindi = 0;

	musbat.forEach((s, indeks) => {
		yigindi += s.value;
		const oxiri = indeks === musbat.length - 1 ? 100 : yumaloq((yigindi / jami) * 100);
		const boshliq = Math.min(BOSHLIQ, (oxiri - boshi) / 2);
		const rangOxiri = yumaloq(oxiri - boshliq);

		natija.push({ color: s.color, from: boshi, to: rangOxiri });
		natija.push({ color: SIRT, from: rangOxiri, to: oxiri });
		boshi = oxiri;
	});

	return natija;
}

/**
 * Donut (CSS `conic-gradient` + markaz teshigi).
 *
 * ⚠️ **Legend HAR DOIM qiymat va foiz bilan** (relief qoidasi, spec
 * §2.1) — burchak kattaligini ko'z bilan solishtirish ishonchsiz. Nol
 * segment legendada QOLADI.
 *
 * ⚠️ `background` qisqartmasi EMAS, `backgroundImage` — test kontrakti
 * (`data-donut`).
 */
export function Donut({
	segments,
	centerValue,
	centerLabel,
	size = 120,
	empty,
	formatValue = String,
}: DonutProps) {
	const stops = donutStops(segments);
	const foizlar = butunFoizlar(segments.map((s) => Math.max(0, s.value)));
	const teshik = Math.round(size * 0.68);
	const tavsif = `${centerLabel}: ${centerValue}. ${segments
		.map((s) => `${s.label}: ${formatValue(s.value)}`)
		.join(", ")}`;

	return (
		<div className="flex flex-wrap items-center gap-4">
			<div className="flex flex-col items-center">
				<div
					data-donut
					role="img"
					aria-label={tavsif}
					className="relative shrink-0 rounded-full"
					style={
						stops.length > 0
							? {
									width: `${size}px`,
									height: `${size}px`,
									backgroundImage: `conic-gradient(${stops
										.map((s) => `${s.color} ${s.from}% ${s.to}%`)
										.join(", ")})`,
								}
							: { width: `${size}px`, height: `${size}px`, backgroundColor: "var(--chart-grid)" }
					}
				>
					<div
						className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full text-center"
						style={{ width: teshik, height: teshik, backgroundColor: SIRT }}
					>
						<span
							data-donut-value
							className="font-semibold text-lg tabular-nums leading-tight"
							style={{ color: "var(--chart-ink)" }}
						>
							{centerValue}
						</span>
						<span data-donut-label className="text-[10px]" style={BOSH_USLUB}>
							{centerLabel}
						</span>
					</div>
				</div>
				{stops.length === 0 && empty ? (
					<div className="mt-2 text-xs" style={BOSH_USLUB}>
						{empty}
					</div>
				) : null}
			</div>

			{/*
			 * ⚠️ `min-w-0` EMAS: u legendni har qanday torlikka siqib,
			 * `flex-wrap` ni hech qachon ishga tushirmasdi (markaz kartasida
			 * qiymatlar chegaradan chiqardi). Minimal kenglikdan tor joyda
			 * legend halqa ostiga o'tadi.
			 */}
			<div className="min-w-[9.5rem] flex-1">
				<Legend
					items={segments.map((s, indeks) => ({
						label: s.label,
						color: s.color,
						value: `${formatValue(s.value)} (${foizlar[indeks] ?? 0}%)`,
					}))}
				/>
			</div>
		</div>
	);
}
